import { useState } from 'react'
import { formatBRL } from '@shared/money/format'
import { sumCents } from '@shared/money/cents'
import type { Category } from '@domain/finance'
import { Glass } from '@ui/glass/Glass'
import { Numero as NumeroDeDisplay } from '@ui/glass/Numero'
import { CategoryPicker } from './CategoryPicker'
import { useFinanceRepositories } from './finance-context'
import { useClassification } from './use-classification'
import type { GroupWithSuggestion } from './use-classification'
import { Voltar } from '@ui/shell/Voltar'

/**
 * Classificar gasto **por estabelecimento**, e não transação por transação.
 *
 * ## A conta que justifica a tela
 *
 * Medido nesta casa: 2158 saídas a classificar, R$ 865.735. Decidir uma a uma
 * são 2158 toques. Agrupadas por estabelecimento viram 552 grupos — e as
 * **50 maiores por valor cobrem 88%** do total. A tela transforma o problema de
 * "classificar tudo" em "tomar cinquenta decisões", ordenadas de forma que
 * parar no meio ainda deixa a maior parte resolvida.
 *
 * ## Ordenada por valor, e o progresso é por valor
 *
 * Por quantidade, dez cafés viriam antes de um aluguel. O número que a pessoa
 * quer ver caindo é o de reais sem categoria, não o de linhas.
 *
 * ## Sugere, não aplica
 *
 * `Merchant.md`: *"Sugere, não aplica — princípio 5."* Nenhuma categoria é
 * escolhida sozinha. O que a tela aprendeu aparece como **sugestão com um
 * botão** — visível, nomeada e recusável —, nunca como categoria já gravada.
 *
 * ## O aprendizado é o que evita refazer isto todo mês
 *
 * Classificar um grupo grava a chave e a categoria na loja
 * (`learnMerchantCategory`). No mês seguinte, o Uber novo chega com "Sugerido:
 * Transporte" e um toque resolve — ou o botão do topo resolve todos de uma vez.
 */
export function Classificar() {
  const estado = useClassification()

  if (estado.status === 'loading') return <Moldura>Lendo o que falta classificar…</Moldura>
  if (estado.status === 'failed') {
    return <Moldura>Não deu para ler as transações. Tente de novo mais tarde.</Moldura>
  }
  return <Lista grupos={estado.groups} categorias={estado.categories} remova={estado.remova} />
}

function Cabecalho() {
  return (
    <>
      <div className="mb-2">
        <Voltar para="/transacoes">Transações</Voltar>
      </div>
      <h1 className="titulo mb-4 text-2xl">Classificar gastos</h1>
    </>
  )
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <section>
      <Cabecalho />
      <Glass level="frost" className="p-5 text-sm text-[var(--text-muted)]">
        <p role="status">{children}</p>
      </Glass>
    </section>
  )
}

function Lista({
  grupos,
  categorias,
  remova,
}: {
  grupos: GroupWithSuggestion[]
  categorias: Category[]
  remova: (key: string) => void
}) {
  const { transactionRepository } = useFinanceRepositories()
  const [aplicandoTudo, setAplicandoTudo] = useState(false)
  const restante = sumCents(grupos.map((g) => g.totalCents))
  const transacoes = grupos.reduce((n, g) => n + g.count, 0)
  const sugeridos = grupos.filter((g) => g.suggestedCategoryId !== null)

  async function apliqueSugestoes() {
    setAplicandoTudo(true)
    for (const g of sugeridos) {
      const categoryId = g.suggestedCategoryId
      if (categoryId === null) continue
      try {
        // Sequencial: cada grupo é uma escrita em lote, e vinte lotes em
        // paralelo sobre a mesma coleção é o que o Firestore penaliza.
        await transactionRepository.updateEnrichmentMany(g.transactionIds, { categoryId })
        remova(g.key)
      } catch (erro: unknown) {
        // O grupo que falhou FICA na lista, com a sugestão dele. Parar tudo
        // aqui desfaria os que já deram certo; seguir em frente deixa o
        // estado honesto e visível.
        console.error('Classificar: sugestão em massa falhou num grupo', erro)
      }
    }
    setAplicandoTudo(false)
  }

  return (
    <section>
      <Cabecalho />

      {grupos.length === 0 ? (
        <Glass level="frost" className="p-5">
          <p role="status" className="text-sm">
            Está tudo classificado. Nada aqui é motivo de orgulho até o próximo sync.
          </p>
        </Glass>
      ) : (
        <>
          <Glass level="frost" className="mb-4 p-5">
            <p className="text-sm text-[var(--text-muted)]">Ainda sem categoria</p>
            {/* Nomeado: o mesmo valor pode aparecer numa linha abaixo quando
                sobra um grupo só, e "o total" precisa ser encontrável sem
                depender de ser o único com aquele texto. */}
            {/* O número que a tela existe para derrubar. Fraunces com
                plaquinha, sobre `frost`. */}
            <p aria-label="Total ainda sem categoria" className="mt-0.5">
              <NumeroDeDisplay tom="pendente" tamanho="grande">
                {formatBRL(restante)}
              </NumeroDeDisplay>
            </p>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              {transacoes} transações em {grupos.length}{' '}
              {grupos.length === 1 ? 'estabelecimento' : 'estabelecimentos'}. Os maiores vêm
              primeiro — os dez de cima costumam valer mais que os quinhentos de baixo.
            </p>
          </Glass>

          {sugeridos.length > 0 ? (
            <Glass level="frost" className="mb-4 flex flex-col gap-2 p-5">
              <p className="text-sm">
                {sugeridos.length === 1
                  ? '1 estabelecimento já foi classificado assim antes.'
                  : `${String(sugeridos.length)} estabelecimentos já foram classificados assim antes.`}{' '}
                Vale {formatBRL(sumCents(sugeridos.map((g) => g.totalCents)))}.
              </p>
              <button
                type="button"
                disabled={aplicandoTudo}
                onClick={() => {
                  void apliqueSugestoes()
                }}
                className="self-start rounded-[var(--radius-md)] bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {aplicandoTudo ? 'Aplicando…' : 'Aplicar todas as sugestões'}
              </button>
            </Glass>
          ) : null}

          <ul className="flex flex-col gap-2">
            {grupos.map((grupo) => (
              <li key={grupo.key}>
                <Grupo grupo={grupo} categorias={categorias} aoClassificar={remova} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function Grupo({
  grupo,
  categorias,
  aoClassificar,
}: {
  grupo: GroupWithSuggestion
  categorias: Category[]
  aoClassificar: (key: string) => void
}) {
  const { transactionRepository, productRepository } = useFinanceRepositories()
  const [gravando, setGravando] = useState(false)
  const [falhou, setFalhou] = useState(false)
  const sugerida = categorias.find((c) => c.id === grupo.suggestedCategoryId) ?? null

  async function classifique(categoryId: string | null) {
    // "Sem categoria" no seletor é o estado em que o grupo já está; tratá-lo
    // como escolha gravaria `null` sobre `null` em N transações.
    if (categoryId === null) return
    setGravando(true)
    setFalhou(false)
    try {
      await transactionRepository.updateEnrichmentMany(grupo.transactionIds, { categoryId })
      // O aprendizado vem DEPOIS da classificação, e a falha dele não desfaz
      // nada: as transações já estão categorizadas, e o pior caso é o mês que
      // vem não vir sugerido. Deixá-lo antes trocaria uma conveniência futura
      // pelo trabalho que a pessoa acabou de fazer.
      await productRepository
        .learnMerchantCategory({ key: grupo.key, label: grupo.label, categoryId })
        .catch((erro: unknown) => {
          console.error('Classificar: não deu para guardar o aprendizado', erro)
        })
      // Sai da lista sem reler: a releitura devolveria exatamente isto, por um
      // preço de milhares de documentos.
      aoClassificar(grupo.key)
    } catch (erro: unknown) {
      console.error('Classificar: gravação em lote falhou', erro)
      setFalhou(true)
      setGravando(false)
    }
  }

  return (
    <Glass level="frost" className="flex flex-col gap-2 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 flex-1 truncate font-medium" title={grupo.label}>
          {grupo.label}
        </span>
        <span className="shrink-0 whitespace-nowrap tabular-nums">
          {formatBRL(grupo.totalCents)}
        </span>
      </div>
      <p className="text-sm text-[var(--text-muted)]">
        {grupo.count === 1 ? '1 transação' : `${String(grupo.count)} transações`}
      </p>
      {sugerida === null ? null : (
        <button
          type="button"
          disabled={gravando}
          onClick={() => {
            void classifique(sugerida.id)
          }}
          className="self-start rounded-[var(--radius-md)] border border-[var(--border)] px-3 py-1 text-sm disabled:opacity-60"
        >
          Sugerido: {sugerida.name} — aplicar
        </button>
      )}
      <CategoryPicker
        categories={categorias}
        value={null}
        disabled={gravando}
        ariaLabel={`Categoria de ${grupo.label}`}
        onChange={(categoryId) => {
          void classifique(categoryId)
        }}
      />
      {gravando ? (
        <p role="status" className="text-sm text-[var(--text-muted)]">
          Classificando {grupo.count === 1 ? 'a transação' : `as ${String(grupo.count)} transações`}
          …
        </p>
      ) : null}
      {falhou ? (
        <p role="alert" className="text-sm text-[var(--text-muted)]">
          Não deu para classificar. Tente de novo.
        </p>
      ) : null}
    </Glass>
  )
}
